import { Fragment, useEffect, useId } from "react";
import { useT } from "../i18n";
import { Erklaerung, Marke } from "./index";
import { Abschnitt, Zeilenblatt } from "./Handyformen";
import { Hakenzeile, Wahlzeile } from "./Postendetails";
import { useZubehoerAuswahl } from "../daten/haken";
import type { Zubehoerlage, Zubehoerstueck } from "../daten/typen";
import { tagKurz as tagKurzImBetrieb } from "../daten/betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Die Anbauteile beim Vermieten — und in der Akte zum Nachsehen.

   Luca, 16.09.2026: *„Wenn man einen Manitou vermietet hat, können wir
   sagen, dass die Schaufel in diesem Zeitraum auch frei ist. Das war der
   Kerngedanke."* Genau das steht hier: nicht „es gibt eine Schaufel",
   sondern **welches Stück im gefragten Zeitraum frei ist**.

   **Eine Komponente, die alles selbst mitbringt**: Sie fragt, was an den
   Typ passt und im Zeitraum frei ist, belegt vor und meldet den Stand
   nach oben. Wer sie einhängt — die Schnellerfassung, das Positionen-
   Formular —, hält nur den Wert und reicht `wert.stuecke` an die API
   (`POST /vorgaenge/:id/positionen`, Feld `zubehoer`). Zwei Auswahlen für
   dieselbe Sache laufen auseinander, und dann ist eine davon die
   schlechtere.

   **Pflicht ist vorbelegt** mit dem ersten freien Stück (Regel 11) — wer
   am Telefon einen Kran mit Korb vermietet, soll nicht auch noch einen
   Korb aussuchen müssen, solange einer frei ist. Welches es wird, bleibt
   änderbar: Das ist Disposition, und die gehört dem Menschen.

   **Was nicht Pflicht ist, steht als Angebot da** — ein Haken, und erst
   dann die Frage nach dem Stück.

   Gewählt wird ein **Stück**, nicht ein Typ: Nur das Stück bekommt eine
   Belegung, und nur dann greift der Ausschluss-Constraint (Regel 8).
   ══════════════════════════════════════════════════════════════════ */

/**
 * Der Stand der Auswahl — alles, was der Einhängende braucht.
 *
 * - `stuecke`: die gewählten Stücke als Ressourcen-UUIDs, genau so, wie
 *   `POST /vorgaenge/:id/positionen` sie im Feld `zubehoer` bindet.
 * - `pflichtFehlt`: die Bezeichnungen des Pflichtzubehörs, für das kein
 *   freies Stück gewählt ist. Solange die Liste nicht leer ist, geht der
 *   Träger nicht hinaus — die Schnellerfassung sperrt dann ihren Knopf.
 * - `laedt`: die Auskunft steht noch aus; auch dann nicht buchen.
 * - `wahl`: welches Stück je Zuordnung gewählt ist. Innenleben — es liegt
 *   im Wert, damit die Wahl bei einem neuen Zeitraum nachrücken kann.
 */
export type ZubehoerWert = {
  wahl: Record<string, string>;
  stuecke: string[];
  pflichtFehlt: string[];
  laedt: boolean;
};

export const ZUBEHOER_LEER: ZubehoerWert = { wahl: {}, stuecke: [], pflichtFehlt: [], laedt: false };

/** `2026-09-18` → `18.09.` — das Jahr steht im Zeitraum, nicht an jedem Stück. */
const tagKurz = (iso: string | null): string => tagKurzImBetrieb(iso); // N8-02

const ersteFreie = (z: Zubehoerlage): string => z.stuecke.find((s) => s.frei)?.id ?? "";

/**
 * Die Wahl an eine neue Lage anpassen — nach einem anderen Zeitraum oder Typ.
 *
 * Was noch frei ist, bleibt gewählt. Ein Pflichtstück, das nicht mehr frei
 * ist, weicht dem ersten freien; ein gewähltes Angebot ebenso. Ein Angebot,
 * das niemand angehakt hat, bleibt leer — vorbelegt wird nur, was sein muss.
 *
 * **Und was der Einhängende schon mitbringt, zählt als „bisher"** (`vorwahl`,
 * 18.09.2026). Ein Posten aus einer Wiederholung trägt die Stücke von damals
 * (`Merkposten.zubehoer`); ohne diesen Weg fing das Blatt am Posten bei Null
 * an, und wer es nur aufmachte und wieder zumachte, hatte das **freiwillige**
 * Zubehör verloren — Pflicht wäre nachgerückt, ein angehakter Löffel nicht.
 *
 * Ein Stück wird dabei höchstens einmal genommen: Derselbe Typ kann an zwei
 * Zuordnungen hängen, und dasselbe Stück stünde dann in beiden.
 */
function wahlVorbelegen(
  lage: Zubehoerlage[],
  alt: Record<string, string>,
  vorwahl: string[] = [],
): Record<string, string> {
  const neu: Record<string, string> = {};
  const vergeben = new Set<string>();
  lage.forEach((z) => {
    const bisher = alt[z.zuordnung_id]
      ?? z.stuecke.find((s) => vorwahl.includes(s.id) && !vergeben.has(s.id))?.id
      ?? "";
    const nochFrei = z.stuecke.some((s) => s.id === bisher && s.frei);
    if (nochFrei) neu[z.zuordnung_id] = bisher;
    else if (z.pflicht || bisher) neu[z.zuordnung_id] = ersteFreie(z);
    else neu[z.zuordnung_id] = "";
    const gesetzt = neu[z.zuordnung_id];
    if (gesetzt) vergeben.add(gesetzt);
  });
  return neu;
}

/**
 * Der Stand einer **bestehenden** Position: Gewählt ist, was gebunden ist.
 *
 * Nicht `wahlVorbelegen` — das legt aus einer leeren Vorwahl die Pflichtstücke
 * fest. An einer Position, die es schon gibt, hieße das: Wer die Zeile nur
 * aufklappt, bindet ein Stück. Hier wird gelesen, nicht entschieden; erst ein
 * Griff des Menschen ändert etwas (18.09.2026, Durchgang B4).
 */
function wahlAusBindung(
  lage: Zubehoerlage[], gebunden: string[], alt: Record<string, string>,
): Record<string, string> {
  const neu: Record<string, string> = {};
  lage.forEach((z) => {
    const bisher = alt[z.zuordnung_id];
    if (bisher !== undefined) { neu[z.zuordnung_id] = bisher; return; }
    neu[z.zuordnung_id] = z.stuecke.find((s) => gebunden.includes(s.id))?.id ?? "";
  });
  return neu;
}

/** Aus Lage und Wahl den Stand, den der Einhängende liest. */
function standAus(lage: Zubehoerlage[] | undefined, wahl: Record<string, string>, laedt: boolean): ZubehoerWert {
  const liste = lage ?? [];
  return {
    wahl,
    stuecke: liste
      .map((z) => wahl[z.zuordnung_id] ?? "")
      .filter((id, i, alle) => id !== "" && alle.indexOf(id) === i),
    pflichtFehlt: liste
      .filter((z) => z.pflicht && !z.stuecke.some((s) => s.id === wahl[z.zuordnung_id] && s.frei))
      .map((z) => z.label),
    laedt,
  };
}

const gleich = (a: ZubehoerWert, b: ZubehoerWert) => JSON.stringify(a) === JSON.stringify(b);

/**
 * **Das Pflichtzubehör, von dem im Zeitraum kein Stück frei ist.**
 *
 * Derselbe Satz wie drüben (`dienste/belegung.ts`, `pflichtOhneStueck`) und
 * dasselbe Ergebnis wie `pflichtFehlt` an der Auswahl oben: Für eine
 * Pflichtzeile belegt `wahlVorbelegen` immer das erste freie Stück vor — es
 * fehlt also genau dann, wenn es keines gibt.
 *
 * **Sie steht hier zusätzlich als reine Funktion**, weil der Abschluss die
 * Frage stellen muss, ohne die Auswahl zu zeichnen: Wer sein Postenblatt nie
 * aufgemacht hat, hat auch nie ein Pflichtstück gewählt — und ein Träger ohne
 * freies Pflichtstück ging damit durch (docs/38-vermietmodul.md, Punkt 2).
 *
 * **Reserviert zählt seit dem 18.09.2026 nicht als frei** (`zubehoerLage`):
 * Ein Stück, das ein anderes Angebot für seine Bindefrist hält, ist keine
 * Zusage, die man nebenbei gibt.
 */
export function pflichtOhneStueck(lage: Zubehoerlage[] | undefined): string[] {
  return (lage ?? []).filter((z) => z.pflicht && z.frei === 0).map((z) => z.label);
}

/** Warum ein Stück nicht frei ist — als Zusatz im Auswahlfeld. */
function stueckZusatz(s: Zubehoerstueck, t: ReturnType<typeof useT>): string {
  if (s.grund === "werkstatt") return t("zubehoer.werkstatt");
  /* Ein angemietetes Stueck, das in dieser Woche nicht bei uns steht, ist
     nicht belegt -- es ist nicht da. "belegt bis Freitag" schickte jemanden
     auf die Suche nach einer Belegung, die es nicht gibt (Migration 0080). */
  if (s.grund === "ausserhalb") return t("zubehoer.ausserhalb");
  if (s.grund === "belegt") return t("zubehoer.belegtBis", { datum: tagKurz(s.belegt_bis) });
  /* Ein reserviertes Stueck ist nicht belegt und trotzdem nicht zu haben: Ein
     anderes Angebot haelt es fuer seine Bindefrist (18.09.2026). */
  if (s.grund === "reserviert") return t("zubehoer.reserviert");
  return s.vorgemerkt ? t("zubehoer.vorgemerkt") : "";
}

/** Die Marken an einer Zuordnung: Pflicht, wie es hinausgeht, Variante, eigener Tarif. */
function Merkmale({ z }: { z: Zubehoerlage }) {
  const t = useT();
  return (
    <>
      {z.pflicht ? <Marke>{t("zubehoer.pflicht")}</Marke> : null}
      {/* Ein Anbauteil, das nur mit einem Träger hinausgeht, steht nirgends
          sonst in der Vermietung — und wer hier abwählt, weiß sonst nicht,
          dass es allein gar nicht zu haben ist (Luca, 16.09.2026). */}
      {z.art === "anbaupflichtig" && !z.pflicht
        ? <span className="zub-leise">{t("zubehoer.artAnbaupflichtig")}</span>
        : null}
      {z.variante ? <span className="zub-leise">{t("zubehoer.nurMitVariante", { variante: z.variante })}</span> : null}
      {/* Nur, wenn es etwas zu sagen gibt: Ein Teil mit eigenem Tarif bekommt
          seine eigene Zeile unter der Miete des Trägers (Migration 0078) —
          gerechnet aus dem Stamm. Wer keinen eigenen Tarif hat, steckt im
          Preis des Trägers, und dann gibt es hier nichts zu melden. */}
      {z.eigener_tarif ? (
        <Marke art="acht">
          {t("zubehoer.eigenerTarif")}
          <Erklaerung k="zubehoer.eigenerTarifErklaerung" />
        </Marke>
      ) : null}
    </>
  );
}

/**
 * Die Auswahl beim Vermieten.
 *
 * ```tsx
 * const [zubehoer, setzeZubehoer] = useState<ZubehoerWert>(ZUBEHOER_LEER);
 * <ZubehoerWahl typId={typ} von={spanne.von} bis={spanne.bis}
 *   wert={zubehoer} aendern={setzeZubehoer} className="angebot-block" />
 * // beim Anlegen: { …, zubehoer: zubehoer.stuecke }
 * ```
 *
 * Steht nichts Passendes am Typ, rendert sie nichts — auch keinen Rahmen und
 * keine Überschrift. Eine leere Zubehörliste bei jedem Radlader wäre Tapete.
 */
export function ZubehoerWahl({
  typId, von, bis, wert, aendern, vorwahl, className, gebunden, handy = false,
}: {
  /**
   * **Die Telefonfassung** (20.09.2026): Zeilen im Hausmaß statt Kästchen
   * und Auswahlfeld — eine Hakenzeile je Anbauteil, darunter die Wahl des
   * Stücks als Wahlzeilen, wenn es mehr als eines gibt. Dieselbe Frage,
   * derselbe Stand, dieselbe Vorbelegung; nur die Zeichnung ist die des
   * Blatts (`bausteine/Postendetails.tsx`, „Das Blatt einer Position am
   * Telefon"). Wer sie setzt, steht in einem `Zeilenblatt`.
   */
  handy?: boolean;
  /** Der Maschinentyp der Miete (UUID). Leer: nichts zu fragen. */
  typId: string | null | undefined;
  /** Der Zeitraum als `YYYY-MM-DD`. Ohne ihn gibt es keine Antwort auf „ist einer frei?". */
  von: string | null | undefined;
  bis: string | null | undefined;
  wert: ZubehoerWert;
  /**
   * Der neue Stand. `vonHand` ist wahr, wenn jemand gewählt hat — und falsch,
   * wenn die Auswahl nur nachrückt (neue Lage, erstes Zeichnen). Wer beim
   * Ändern sofort schreibt, schreibt nur bei `vonHand` (V-19, 03.10.2026).
   */
  aendern: (neu: ZubehoerWert, vonHand?: boolean) => void;
  /**
   * Stücke, die schon gebunden sind — Ressourcen-UUIDs (18.09.2026).
   *
   * Sie gelten als „war schon gewählt", solange der eigene Stand leer ist: Ein
   * Posten aus einer Wiederholung bringt sie mit (`Merkposten.zubehoer`), und
   * ohne sie verlöre das Blatt beim ersten Öffnen alles, was nicht Pflicht ist.
   */
  vorwahl?: string[] | undefined;
  /** Der Rahmen, in dem sie steht — `se-block`, `angebot-block`. Nur, wenn sie etwas zeigt. */
  className?: string;
  /**
   * Die Stücke, die an dieser Position **schon gebunden** sind (Ressourcen-UUIDs).
   * Mit ihnen liest die Wahl den Stand, statt Pflichtstücke vorzubelegen — und
   * ein gebundenes Stück bleibt wählbar, auch wenn die eigene Belegung es
   * „belegt" nennt (18.09.2026).
   */
  gebunden?: string[] | undefined;
}) {
  const t = useT();
  const id = useId();
  const aktiv = Boolean(typId && von && bis);
  const abfrage = useZubehoerAuswahl(typId || undefined, von, bis);
  const lage = aktiv ? abfrage.data : undefined;
  const laedt = aktiv && abfrage.isLoading;

  /* Kommt eine neue Lage — anderer Typ, anderer Zeitraum —, rückt die Wahl
     nach und der Stand geht nach oben. Nur, wenn er sich wirklich ändert:
     Sonst meldet jede Meldung die nächste. */
  useEffect(() => {
    /* **`gebunden` schlägt `vorwahl`.** Beides kam am 18.09.2026 dazu und
       beantwortet verschiedene Fragen: `vorwahl` ist ein Vorschlag für eine
       NEUE Zeile (aus einer Wiederholung), `gebunden` der Stand einer
       BESTEHENDEN Position. Wo etwas gebunden ist, wird gelesen und nicht
       vorgeschlagen — sonst wählte das Öffnen einer alten Zeile etwas ab. */
    const wahl = lage
      ? (gebunden ? wahlAusBindung(lage, gebunden, wert.wahl) : wahlVorbelegen(lage, wert.wahl, vorwahl))
      : {};
    const neu = standAus(lage, wahl, laedt);
    if (!gleich(neu, wert)) aendern(neu, false);
  }, [lage, laedt, wert, aendern, vorwahl, gebunden]);

  if (!lage?.length) return null;

  const setze = (zuordnung: string, stueck: string) =>
    aendern(standAus(lage, { ...wert.wahl, [zuordnung]: stueck }, laedt), true);

  if (handy) {
    const trenn = ` ${t("allgemein.trennpunkt")} `;
    return (
      <Abschnitt titel={t("zubehoer.titel")}>
        <Zeilenblatt>
        {lage.map((z) => {
          const gewaehlt = wert.wahl[z.zuordnung_id] ?? "";
          const eigenes = z.stuecke.some((s) => gebunden?.includes(s.id));
          const keinsFrei = z.frei === 0 && !eigenes;
          const fehlt = z.pflicht && keinsFrei;
          /* Die zweite Zeile: Pflicht, wie es hinausgeht, Variante, eigener
             Tarif — dieselben Wörter wie am Schreibtisch (`Merkmale`). */
          const unter = [
            z.pflicht ? t("zubehoer.pflicht") : "",
            z.art === "anbaupflichtig" && !z.pflicht ? t("zubehoer.artAnbaupflichtig") : "",
            z.variante ? t("zubehoer.nurMitVariante", { variante: z.variante }) : "",
            z.eigener_tarif ? t("zubehoer.eigenerTarif") : "",
            z.stuecke.length === 0 ? t("zubehoer.keinStueck")
              : keinsFrei ? t("zubehoer.keinsFrei")
                : t("zubehoer.zahlFrei", { frei: z.frei, alle: z.stuecke.length }),
          ].filter(Boolean).join(trenn);
          const stueck = z.stuecke.find((s) => s.id === gewaehlt);
          /* Mehr als ein Stück zur Wahl: Die Kennungen stehen als Wahlzeilen
             darunter — nur, solange das Teil gewählt ist. */
          const zurWahl = gewaehlt !== "" && z.stuecke.length > 1 && (z.frei > 0 || eigenes);
          return (
            <Fragment key={z.zuordnung_id}>
              <Hakenzeile
                wort={z.label}
                unter={unter}
                rechts={stueck ? <span className="kennung">{stueck.kennung}</span> : undefined}
                an={gewaehlt !== ""}
                /* Pflicht steht nicht zur Wahl — der Haken ist gesetzt und
                   bleibt es; abgewählt wird nur, was freiwillig ist. */
                gesperrt={z.pflicht || keinsFrei}
                grund={z.pflicht ? t("zubehoer.pflichtErklaerung") : undefined}
                pruefen={fehlt}
                aendern={(an) => setze(z.zuordnung_id, an ? ersteFreie(z) : "")}
              />
              {zurWahl ? (
                <div className="posb-unterwahl">
                  {z.stuecke.map((s) => {
                    const zusatz = stueckZusatz(s, t);
                    return (
                      <Wahlzeile
                        key={s.id}
                        wort={<span className="kennung">{s.kennung}</span>}
                        unter={zusatz || undefined}
                        an={gewaehlt === s.id}
                        gesperrt={!s.frei && !gebunden?.includes(s.id)}
                        waehlen={() => setze(z.zuordnung_id, s.id)}
                      />
                    );
                  })}
                </div>
              ) : null}
            </Fragment>
          );
        })}
        </Zeilenblatt>
      </Abschnitt>
    );
  }

  return (
    <div className={className}>
      <span className="feld-schild">
        {t("zubehoer.titel")}
        {lage.some((z) => z.pflicht) ? <Erklaerung k="zubehoer.pflichtErklaerung" /> : null}
      </span>
      <div className="zub-liste">
        {lage.map((z) => {
          const gewaehlt = wert.wahl[z.zuordnung_id] ?? "";
          const feld = `${id}-${z.zuordnung_id}`;
          /* Was an dieser Position hängt, zählt wie frei: Die eigene Belegung
             macht es „belegt", und ohne diese Ausnahme verschwände das
             gebuchte Stück aus seiner eigenen Auswahl. */
          const eigenes = z.stuecke.some((s) => gebunden?.includes(s.id));
          const zeigtStueck = (z.pflicht || gewaehlt !== "") && (z.frei > 0 || eigenes);
          return (
            <div
              className={`zub-zeile${z.pflicht && z.frei === 0 && !z.stuecke.some((s) => gebunden?.includes(s.id)) ? " fehlt" : ""}`}
              key={z.zuordnung_id}
            >
              <div className="zub-kopf">
                {z.pflicht ? (
                  <b className="zub-name">{z.label}</b>
                ) : (
                  <label className="hakenzeile zub-name" htmlFor={`${feld}-mit`}>
                    <input
                      id={`${feld}-mit`}
                      type="checkbox"
                      checked={gewaehlt !== ""}
                      disabled={z.frei === 0}
                      onChange={(e) => setze(z.zuordnung_id, e.target.checked ? ersteFreie(z) : "")}
                    />
                    <b>{z.label}</b>
                  </label>
                )}
                <Merkmale z={z} />
              </div>

              {z.stuecke.length === 0 ? (
                <span className="zub-leise">{t("zubehoer.keinStueck")}</span>
              ) : z.frei === 0 && !eigenes ? (
                <span className="zub-lage">
                  <Marke art={z.pflicht ? "stopp" : ""} punkt={z.pflicht}>{t("zubehoer.keinsFrei")}</Marke>
                  <span className="zub-leise">
                    {z.stuecke.map((s) => `${s.kennung} ${stueckZusatz(s, t)}`).join(" · ")}
                  </span>
                </span>
              ) : zeigtStueck ? (
                <select
                  id={feld}
                  className="fein mono zub-stueck"
                  aria-label={t("zubehoer.stueckWahl", { label: z.label })}
                  value={gewaehlt}
                  onChange={(e) => setze(z.zuordnung_id, e.target.value)}
                >
                  {z.stuecke.map((s) => (
                    <option key={s.id} value={s.id} disabled={!s.frei && !gebunden?.includes(s.id)}>
                      {s.kennung}{stueckZusatz(s, t) ? ` · ${stueckZusatz(s, t)}` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="zub-leise">{t("zubehoer.zahlFrei", { frei: z.frei, alle: z.stuecke.length })}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Zum Nachsehen in der Akte: was an diesen Typ passt und welches Stück heute
 * frei ist. Keine Auswahl — gebucht wird beim Vermieten, nicht in der Akte.
 */
export function ZubehoerUebersicht({ lage }: { lage: Zubehoerlage[] }) {
  const t = useT();
  return (
    <div className="zub-liste">
      {lage.map((z) => (
        <div className="zub-zeile" key={z.zuordnung_id}>
          <div className="zub-kopf">
            <b className="zub-name">{z.label}</b>
            <Merkmale z={z} />
          </div>
          {z.stuecke.length === 0 ? (
            <span className="zub-leise">{t("zubehoer.keinStueck")}</span>
          ) : (
            <span className="zub-stuecke">
              {z.stuecke.map((s) => (
                <span key={s.id} className={`zub-chip${s.frei ? " frei" : ""}`}>
                  <span className="kennung">{s.kennung}</span>
                  {s.frei ? t("zubehoer.frei") : stueckZusatz(s, t)}
                </span>
              ))}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
